export const services = [
  {
    slug: "cybersecurity",
    icon: "ShieldCheck",
    seoTitle: "Cybersecurity Services & Consulting | Aliwer",
    seoDescription:
      "Security assessments, penetration testing, threat modeling, architecture, and security engineering for growing organizations.",
    title: "Cybersecurity Services & Consulting",
    card: "End-to-end security assessments, testing, architecture, and engineering for growing organizations.",
    intro:
      "Aliwer helps organizations understand their risk, harden their systems, and build security into the way they operate. We combine hands-on testing with practical engineering so recommendations actually get shipped.",
    whatWeDo: [
      "Security assessments that prioritize risk by real business impact.",
      "Penetration testing and validation of exploitable weaknesses.",
      "Threat modeling and secure architecture reviews.",
      "Security engineering to remediate findings, not just report them.",
    ],
    deliverables: [
      "An evidence-driven findings report with prioritized risk ratings.",
      "A remediation roadmap mapped to owners and effort.",
      "Architecture and control recommendations.",
      "A debrief with your technical and leadership teams.",
    ],
    whoFor:
      "Organizations that need a clear, prioritized picture of their security posture and a partner to help fix what matters most.",
  },
  {
    slug: "security-assessment",
    icon: "ScanSearch",
    seoTitle: "Cybersecurity Assessment Services | Aliwer",
    seoDescription:
      "Identify critical security gaps, prioritize risk, and receive an evidence-driven remediation roadmap built around your environment.",
    title: "Cybersecurity Assessment Services",
    card: "Find critical gaps and get an evidence-driven remediation roadmap for your environment.",
    intro:
      "Our assessments give you an honest, prioritized view of where you're exposed — and a practical plan to close the highest-impact gaps first.",
    whatWeDo: [
      "Review your environment, architecture, and controls.",
      "Identify and rank gaps by exploitability and business impact.",
      "Benchmark against recognized frameworks and best practices.",
      "Translate findings into an actionable remediation roadmap.",
    ],
    deliverables: [
      "Prioritized risk register with severity ratings.",
      "Evidence-driven findings with clear reproduction detail.",
      "Remediation roadmap with quick wins and longer-term items.",
      "Executive summary for leadership.",
    ],
    whoFor:
      "Teams that need a baseline of their security posture, or a periodic check before an audit, funding round, or major launch.",
  },
  {
    slug: "penetration-testing",
    icon: "Crosshair",
    seoTitle: "Penetration Testing Services | Aliwer",
    seoDescription:
      "Find exploitable weaknesses before attackers do with scoped penetration testing, validation, reporting, and remediation guidance.",
    title: "Penetration Testing Services",
    card: "Scoped penetration testing that finds exploitable weaknesses before attackers do.",
    intro:
      "We think like an attacker to find the paths that matter — then give you the detail and guidance to close them for good.",
    whatWeDo: [
      "Scope and rules of engagement tailored to your environment.",
      "Manual and automated testing across your attack surface.",
      "Exploit validation to separate real risk from noise.",
      "Retesting to confirm fixes hold.",
    ],
    deliverables: [
      "Detailed report with reproduction steps and evidence.",
      "Risk-rated findings mapped to remediation guidance.",
      "Executive summary and technical debrief.",
      "Free retest of remediated findings within the agreed window.",
    ],
    whoFor:
      "Organizations that need proof of exploitability, meet a compliance requirement, or want assurance before a release.",
  },
  {
    slug: "application-security",
    icon: "Code2",
    seoTitle: "Application Security & DevSecOps | Aliwer",
    seoDescription:
      "Build safer software with threat modeling, secure SDLC, code review, SAST, DAST, dependency controls, and security verification.",
    title: "Application Security & DevSecOps",
    card: "Build safer software with secure SDLC, code review, SAST/DAST, and verification.",
    intro:
      "We embed security into your development lifecycle so vulnerabilities are caught early — and shipping fast doesn't mean shipping risk.",
    whatWeDo: [
      "Threat modeling and secure design reviews.",
      "Secure SDLC and DevSecOps pipeline integration.",
      "Manual code review plus SAST, DAST, and dependency scanning.",
      "Security verification and developer enablement.",
    ],
    deliverables: [
      "Threat model and prioritized security backlog.",
      "Pipeline security tooling configured and integrated.",
      "Code review findings with fix guidance.",
      "Developer guidance and secure coding standards.",
    ],
    whoFor:
      "Product and engineering teams that want to ship secure software without slowing delivery.",
  },
  {
    slug: "threat-modeling",
    icon: "Network",
    seoTitle: "Threat Modeling Services | Aliwer",
    seoDescription:
      "Identify attack paths, trust boundaries, abuse cases, and security controls before vulnerabilities become expensive problems.",
    title: "Threat Modeling Services",
    card: "Map attack paths, trust boundaries, and controls before problems get expensive.",
    intro:
      "Threat modeling finds design-level risk early — the cheapest and most effective time to address it.",
    whatWeDo: [
      "Map data flows, trust boundaries, and assets.",
      "Identify attack paths and abuse cases.",
      "Recommend controls proportionate to risk.",
      "Integrate threat modeling into your design process.",
    ],
    deliverables: [
      "Threat model diagrams and documented assumptions.",
      "Prioritized list of threats and recommended controls.",
      "Repeatable threat-modeling process for your team.",
      "Design review debrief.",
    ],
    whoFor:
      "Teams designing new systems or features who want to prevent expensive security rework later.",
  },
  {
    slug: "cloud-security",
    icon: "Cloud",
    seoTitle: "Cloud Security Consulting | Aliwer",
    seoDescription:
      "Secure cloud architecture, identity, logging, configuration, workloads, and governance across modern cloud environments.",
    title: "Cloud Security Consulting",
    card: "Secure cloud architecture, identity, logging, configuration, and governance.",
    intro:
      "We help you architect, migrate, and operate cloud environments securely across AWS, Azure, and Google Cloud.",
    whatWeDo: [
      "Cloud architecture and configuration reviews.",
      "Identity, access, and least-privilege design.",
      "Logging, monitoring, and detection setup.",
      "Cloud governance and guardrails.",
    ],
    deliverables: [
      "Configuration and posture findings with fixes.",
      "Identity and access recommendations.",
      "Logging and detection baseline.",
      "Cloud governance guardrails and policies.",
    ],
    whoFor:
      "Cloud-first and cloud-migrating organizations that need secure, well-governed cloud environments.",
  },
  {
    slug: "ai-security",
    icon: "Cpu",
    seoTitle: "AI Security & AI Risk Assessment | Aliwer",
    seoDescription:
      "Assess AI data exposure, model risks, access controls, prompt threats, governance, and secure AI integration.",
    title: "AI Security & AI Risk Assessment",
    card: "Assess AI data exposure, model risk, prompt threats, and secure AI integration.",
    intro:
      "As teams adopt AI, new risks emerge. We help you deploy AI safely — with governance, controls, and clear-eyed risk assessment.",
    whatWeDo: [
      "Assess AI data exposure and model risks.",
      "Review access controls and prompt-injection threats.",
      "Establish AI governance and acceptable-use policy.",
      "Guide secure AI integration into your products.",
    ],
    deliverables: [
      "AI risk assessment with prioritized findings.",
      "Governance and acceptable-use policy.",
      "Control recommendations for data and access.",
      "Secure integration guidance.",
    ],
    whoFor:
      "Organizations building or adopting AI who need to manage data, model, and governance risk.",
  },
  {
    slug: "compliance",
    icon: "ClipboardCheck",
    seoTitle: "Cybersecurity Compliance Consulting | Aliwer",
    seoDescription:
      "Build practical security and compliance programs with controls, evidence, risk management, and audit-ready documentation.",
    title: "Cybersecurity Compliance Consulting",
    card: "Practical security and compliance programs with controls, evidence, and audit-readiness.",
    intro:
      "We turn compliance from a checkbox exercise into a practical security program — audit-ready and genuinely useful.",
    whatWeDo: [
      "Gap analysis against SOC 2, ISO 27001, HIPAA, and GDPR.",
      "Design and implement controls and policies.",
      "Build evidence collection and risk management.",
      "Prepare you for audit and support the process.",
    ],
    deliverables: [
      "Gap analysis and remediation plan.",
      "Policies, controls, and evidence framework.",
      "Risk register and treatment plan.",
      "Audit-ready documentation and support.",
    ],
    whoFor:
      "Organizations pursuing certification or meeting customer, regulatory, or contractual security requirements.",
  },
  {
    slug: "managed-security",
    icon: "Activity",
    seoTitle: "Managed Security Services | Aliwer",
    seoDescription:
      "Extend your security team with monitoring, MDR, incident response, vulnerability management, and continuous security operations.",
    title: "Managed Security Services",
    card: "Monitoring, MDR, incident response, and continuous security operations.",
    intro:
      "Get a security operations capability without the overhead — continuous monitoring, detection, and response tuned to your environment.",
    whatWeDo: [
      "Ongoing security monitoring and management.",
      "Managed detection and response (MDR).",
      "Incident response and containment.",
      "Ongoing vulnerability management.",
    ],
    deliverables: [
      "Continuous monitoring and alerting.",
      "Detection and response playbooks.",
      "Incident response support and reporting.",
      "Regular posture and vulnerability reviews.",
    ],
    whoFor:
      "Teams that need round-the-clock security operations but can't staff a full in-house SOC.",
  },
  {
    slug: "vciso",
    icon: "Briefcase",
    seoTitle: "vCISO Services & Security Leadership | Aliwer",
    seoDescription:
      "Get fractional security leadership for risk strategy, governance, security programs, board reporting, and compliance readiness.",
    title: "vCISO Services & Security Leadership",
    card: "Fractional security leadership for strategy, governance, and board reporting.",
    intro:
      "Get executive security leadership on a fractional basis — strategy, governance, and program ownership without a full-time hire.",
    whatWeDo: [
      "Set risk strategy and security roadmap.",
      "Build and run the security program.",
      "Provide board and leadership reporting.",
      "Drive compliance readiness and vendor risk.",
    ],
    deliverables: [
      "Security strategy and prioritized roadmap.",
      "Governance structure and program ownership.",
      "Board-ready reporting and metrics.",
      "Compliance and vendor-risk oversight.",
    ],
    whoFor:
      "Organizations that need senior security leadership and accountability without a full-time CISO.",
  },
];
